import type { JournalVoiceMemo } from '../../lib/data'
import { clockFromTimestamp, durationPhrase } from '../../lib/time'

// ============================================================================
// A VOICE MEMO, INLINE IN THE DAY — docs/design/handoff/"LifeOS Journal.dc.html",
// option 5b: memos render at the position they were dropped, not in a sidebar.
//
// THE PLAYER DOES NOT PLAY, AND SAYS SO. Lecture and memo audio is archived on the
// owner's PC (README §5, and CLAUDE.md → Architecture); the upload path and the
// desktop worker are the whole of V1. So the control is `disabled`, carries a
// title that names what is missing, and the group carries one quiet line beneath
// it. The mockup also draws a 34% progress fill — that is a PLAYHEAD, and a
// playhead over audio that cannot be played is invented state, so the track is
// drawn empty.
// ============================================================================

export function DayMemo({ memo }: { memo: JournalVoiceMemo }) {
  const transcribed = memo.status === 'transcribed'

  return (
    <div
      className={[
        'mt-4 rounded-panel border border-line-4 bg-surface-2 px-4 py-[14px]',
        transcribed ? 'flex flex-col gap-[11px]' : 'flex items-center gap-[11px]',
      ].join(' ')}
    >
      <div className={transcribed ? 'flex items-center gap-[11px]' : 'contents'}>
        <button
          type="button"
          disabled
          title="The audio is archived on your PC. Playback from this page arrives with V1 — the desktop worker and the upload path."
          aria-label={`Play the memo recorded at ${clockFromTimestamp(memo.recordedAt)} (not available yet)`}
          className={[
            'flex size-[30px] flex-none items-center justify-center rounded-full',
            transcribed ? 'bg-success-dot' : 'bg-line-2',
          ].join(' ')}
        >
          <span
            className={[
              'ml-[2px] size-0 border-y-[5px] border-l-[8px] border-y-transparent',
              transcribed ? 'border-l-surface' : 'border-l-muted',
            ].join(' ')}
            aria-hidden
          />
        </button>

        <div className="flex flex-1 flex-col gap-[6px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold leading-none text-ink-2">
              Recorded {clockFromTimestamp(memo.recordedAt)}
            </span>
            <span className="text-[11px] leading-none text-microcopy">
              · {durationPhrase(memo.durationSec)}
              {transcribed && ' · transcribed'}
            </span>
            {memo.status === 'queued' && (
              <span className="ml-[6px] flex items-center gap-[6px]">
                <span
                  className="size-[11px] rounded-full border-[1.6px] border-line-2 border-t-microcopy"
                  aria-hidden
                />
                {/* The handoff's rule for this state: a queued memo NAMES the thing it is waiting
                    on. The wording matches the Today composer's row so one memo does not describe
                    itself two ways on two screens. */}
                <span className="text-[10.5px] leading-none text-faint">
                  waiting for desktop worker
                </span>
              </span>
            )}
            {memo.status === 'recording' && (
              <span className="ml-[6px] flex items-center gap-[6px]">
                <span className="size-[11px] rounded-full bg-danger-dot" aria-hidden />
                <span className="text-[10.5px] leading-none text-faint">recording now</span>
              </span>
            )}
            {/* A memo the desktop worker gave up on. It is NOT hidden and it does NOT say
                "waiting": the audio is still archived on the PC, and the reason is the worker's
                own sentence rather than this component's guess at one. */}
            {memo.status === 'failed' && (
              <span className="ml-[6px] flex items-center gap-[6px]">
                <span className="size-[11px] rounded-full border-[1.6px] border-danger-dot" aria-hidden />
                <span className="text-[10.5px] leading-[1.4] text-danger text-pretty">
                  {memo.error ?? 'the desktop worker could not transcribe this one'}
                </span>
              </span>
            )}
          </div>

          {transcribed ? (
            <div className="h-1 rounded-[3px] bg-line" aria-hidden />
          ) : (
            <div className="flex h-[14px] items-center gap-[2px]" aria-hidden>
              {[6, 11, 8, 14, 7, 12, 9, 13, 6, 10].map((h, i) => (
                <span
                  key={i}
                  className="w-[2px] rounded-[2px] bg-dash"
                  style={{ height: `${h}px` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {transcribed && memo.transcriptText && (
        <blockquote className="border-l-2 border-line pl-[41px] text-[14.5px] leading-[1.7] text-ink-3 text-pretty">
          {memo.transcriptText}
        </blockquote>
      )}
    </div>
  )
}

/**
 * One line under the memos on a day, rather than one per memo: the audio lives on a machine that
 * is not this one, and saying so once is honest without being nagging.
 */
export function MemoAudioNote() {
  return (
    <p className="mt-2 text-[10.5px] leading-[1.5] text-faint text-pretty">
      Audio is archived on your PC · transcripts are in the cloud. Playing a memo from this page
      arrives with V1.
    </p>
  )
}
